import { html, Html, View } from 'rune-ts';
import { ProductBadgeOption } from '@services/MarppleShop/features/ProductBadge';
import { ProductTimeHelper } from '@services/MarppleShop/renderApp/components/cells/ProductCard/utils/ProductTimeHelper.ts';
import { LaunchIcon, MembershipIcon } from '@services/MarppleShop/renderApp/components/atoms/Icon';
import { isNumber } from '@fxts/core';
import klass from './ProductBadgeView.module.scss';
import { font, staticFont } from '@services/MarppleShop/shared/typography';

export type TProductBadgeViewProps =
  | { type: 'Trend' }
  | { type: 'Popular' }
  | { type: 'Membership' }
  | { type: 'Nft' }
  | { type: 'Limited' }
  | { type: 'BrandDeal' }
  | { type: 'SoldOut' }
  | { type: 'Funding' }
  | { type: 'Closed' }
  | {
      type: 'Time_left';
      endDate: Date;

      // 값이 작을수록 높은 우선순위
      priority: number;
    }
  | {
      type: 'Stock_left';
      availableStock: number;

      // 값이 작을수록 높은 우선순위
      priority: number | null;
    }
  | {
      type: 'Rank';
      rank: number;
    }
  | {
      type: 'Upcoming';
      startDate: Date;
    };

type TProps = TProductBadgeViewProps & {
  options: ProductBadgeOption;
};

export class ProductBadgeView extends View<TProps> {
  protected override template(): Html {
    const getSizeClass = () => {
      if (this.data.options.size === 'small') return klass.small;
      if (this.data.options.size === 'medium') return klass.medium;
      if (this.data.options.size === 'large') return klass.large;
      return klass.medium; // default
    };

    const getFontClass = () => {
      if (this.data.options.size === 'small') return font('10_medium');
      if (this.data.options.size === 'medium') return font('14_medium');
      return '';
    };

    const sizeClass = getSizeClass();
    const fontClass = getFontClass();
    const customClass = this.data.options.klass || '';

    if (this.data.type === 'Rank') {
      const colorClass =
        isNumber(this.data.rank) && this.data.rank <= 3 ? klass.blue_filled : klass.skyblue_filled;
      const shapeClass = this.data.options?.ratio === 'square' ? klass.square : '';

      if (this.data.options.size === 'large') {
        return html`<span
          class="${klass.product_badge} ${colorClass} ${shapeClass} ${klass.large} ${staticFont(
            'bebas_14_bold',
          )} ${customClass} ${fontClass}"
        >
          <span class="${klass.label}">${this.data.rank}</span>
        </span>`;
      } else if (this.data.options.size === 'xLarge') {
        return html`<span
          class="${klass.product_badge} ${colorClass} ${shapeClass} ${klass.xLarge} ${staticFont(
            'bebas_16_bold',
          )} ${customClass} ${fontClass}"
        >
          <span class="${klass.label}">${this.data.rank}</span>
        </span>`;
      }
    } else if (this.data.type === 'Trend') {
      const label = ET('mps2::product::badge::trending', { lng: this.data.options.lang });

      return html`<span
        class="${klass.product_badge} ${klass.orange_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">${label}</span>
      </span>`;
    } else if (this.data.type === 'Popular') {
      const label = ET('mps2::product::badge::popular', { lng: this.data.options.lang });

      return html`<span
        class="${klass.product_badge} ${klass.orange_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">${label}</span>
      </span>`;
    } else if (this.data.type === 'BrandDeal') {
      const label = ET('mps2::product::badge::brand_deal', { lng: this.data.options.lang });
      return html`<span
        class="${klass.product_badge} ${klass.blue_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">${label}</span>
      </span>`;
    } else if (this.data.type === 'Limited') {
      const label = ET('mps2::product::badge::limited', { lng: this.data.options.lang });
      return html`<span
        class="${klass.product_badge} ${klass.blue_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">${label}</span>
      </span>`;
    } else if (this.data.type === 'Stock_left') {
      let label;

      if (this.data.options.lang === 'kr') {
        if (this.data.availableStock >= 100_000_000) {
          const value = Math.floor(this.data.availableStock / 100_000_000);
          label = `${value}억 개 남음`;
        } else if (this.data.availableStock >= 10_000) {
          const value = Math.floor(this.data.availableStock / 10_000);
          label = `${value}만 개 남음`;
        } else if (this.data.availableStock >= 1_000) {
          const value = Math.floor(this.data.availableStock / 1_000);
          label = `${value}천 개 남음`;
        } else {
          label = `${this.data.availableStock}개 남음`;
        }
      } else if (this.data.options.lang === 'jp') {
        if (this.data.availableStock >= 100_000_000) {
          const value = Math.floor(this.data.availableStock / 100_000_000);
          label = `残り${value}億点`;
        } else if (this.data.availableStock >= 10_000) {
          const value = Math.floor(this.data.availableStock / 10_000);
          label = `残り${value}万点`;
        } else if (this.data.availableStock >= 1_000) {
          const value = Math.floor(this.data.availableStock / 1_000);
          label = `残り${value}千点`;
        } else {
          label = `残り${this.data.availableStock}点`;
        }
      } else if (this.data.options.lang === 'en') {
        if (this.data.availableStock >= 1_000_000_000) {
          const value = Math.floor(this.data.availableStock / 1_000_000_000);
          label = `${value}B Left`;
        } else if (this.data.availableStock >= 1_000_000) {
          const value = Math.floor(this.data.availableStock / 1_000_000);
          label = `${value}M Left`;
        } else if (this.data.availableStock >= 1_000) {
          const value = Math.floor(this.data.availableStock / 1_000);
          label = `${value}K Left`;
        } else {
          label = `${this.data.availableStock} Left`;
        }
      }

      return html`<span
        class="${klass.product_badge} ${klass.blue_text} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">${label}</span>
      </span>`;
    } else if (this.data.type === 'Time_left') {
      if (this.data.endDate && this.data.options.baseTimestamp) {
        const timeDiff = ProductTimeHelper.diffDate(
          new Date(this.data.endDate),
          new Date(this.data.options.baseTimestamp),
        );

        if (timeDiff.day > 0) {
          return html`<span
            class="${klass.product_badge} ${klass.blue_text} ${sizeClass} ${customClass} ${fontClass}"
          >
            <span class="${klass.label}">
              ${ET('mps2::product::badge::period_limited_day', {
                day: timeDiff.day,
                lng: this.data.options.lang,
              })}
            </span>
          </span>`;
        } else if (timeDiff.hour > 0) {
          return html`<span
            class="${klass.product_badge} ${klass.blue_text} ${sizeClass} ${customClass} ${fontClass}"
          >
            <span class="${klass.label}">
              ${ET('mps2::product::badge::period_limited_hour', {
                hour: timeDiff.hour,
                lng: this.data.options.lang,
              })}
            </span>
          </span>`;
        } else if (timeDiff.minute > 0) {
          return html`<span
            class="${klass.product_badge} ${klass.blue_text} ${sizeClass} ${customClass} ${fontClass}"
          >
            <span class="${klass.label}">
              ${ET('mps2::product::badge::period_limited_minute', {
                minute: timeDiff.minute,
                lng: this.data.options.lang,
              })}
            </span>
          </span>`;
        } else if (timeDiff.second >= 0) {
          return html`<span
            class="${klass.product_badge} ${klass.blue_text} ${sizeClass} ${customClass} ${fontClass}"
          >
            <span class="${klass.label}">
              ${ET('mps2::product::badge::period_limited_second', {
                second: timeDiff.second,
                lng: this.data.options.lang,
              })}
            </span>
          </span>`;
        }
      }
    } else if (this.data.type === 'Upcoming') {
      const diff = ProductTimeHelper.diffDate(this.data.startDate, new Date(this.data.options.baseTimestamp));
      const options = { ...diff, lng: this.data.options.lang };

      let label: string;
      if (diff.day >= 4 || diff.second < 0) {
        label = ET('mps2::product::badge::upcoming', options);
      } else if (diff.day > 0) {
        label = ET('mps2::product::badge::period_limited_day', options);
      } else if (diff.hour > 0) {
        label = ET('mps2::product::badge::period_limited_hour', options);
      } else if (diff.minute > 0) {
        label = ET('mps2::product::badge::period_limited_minute', options);
      } else if (diff.second > 0) {
        label = ET('mps2::product::badge::period_limited_second', options);
      } else {
        label = ET('mps2::product::badge::upcoming', options);
      }

      return html`<span
        class="${klass.product_badge} ${klass.pink_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.icon_medium}">${LaunchIcon}</span>
        <span class="${klass.label}">${label}</span>
      </span>`;
    } else if (this.data.type === 'Membership') {
      if (this.data.options.state === 'MembershipWithLabel') {
        return html`<span
          class="${klass.product_badge} ${klass.black_filled} ${sizeClass} ${customClass} ${fontClass}"
        >
          <span class="${klass.icon_medium}">${MembershipIcon}</span>
          <span class="${klass.label}">${ET('mps2::product::badge::membership')}</span>
        </span>`;
      } else if (this.data.options.state !== 'MembershipWithLabel' || this.data.options.hide_label) {
        return html`<span
          class="${klass.product_badge} ${klass.square} ${klass.black_filled} ${sizeClass} ${customClass} ${fontClass}"
        >
          <span class="${klass.icon_medium}">${MembershipIcon}</span>
        </span>`;
      }
    } else if (this.data.type === 'Nft') {
      return html`<span
        class="${klass.product_badge} ${klass.purple_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">
          ${ET('mps2::product::badge::nft', { lng: this.data.options.lang })}
        </span>
      </span>`;
    } else if (this.data.type === 'SoldOut') {
      return html`<span
        class="${klass.product_badge} ${klass.gray_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">
          ${ET('mps2::product::badge::sold_out', { lng: this.data.options.lang })}
        </span>
      </span>`;
    } else if (this.data.type === 'Funding') {
      return html`<span
        class="${klass.product_badge} ${klass.blue_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">${ET('preorder::challenge', { lng: this.data.options.lang })} </span>
      </span>`;
    } else if (this.data.type === 'Closed') {
      return html`<span
        class="${klass.product_badge} ${klass.gray_filled} ${sizeClass} ${customClass} ${fontClass}"
      >
        <span class="${klass.label}">
          ${ET('mps2::product::badge::closed', { lng: this.data.options.lang })}
        </span>
      </span>`;
    }

    throw new Error(this.data.type);
  }
}
